import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faXmark,
  faCircleCheck,
  faCircleXmark,
  faTriangleExclamation,
  faClock,
  faCopy,
  faCode,
  faChevronDown,
  faChevronUp,
  faArrowRotateRight,
} from "@fortawesome/free-solid-svg-icons";

/**
 * Isolated collapsible technical JSON viewer so each card manages its own open/copied state independently.
 */
const TechnicalJsonViewer = ({ data, label = "Technical JSON Response" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!data) return null;

  const handleCopy = (e) => {
    e.stopPropagation();
    try {
      const text = typeof data === "object" ? JSON.stringify(data, null, 2) : String(data);
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  return (
    <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden", marginTop: "12px" }}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          padding: "9px 14px",
          background: "#f8fafc",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <span style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>
          <FontAwesomeIcon icon={faCode} className="me-2" /> {label}
        </span>
        <FontAwesomeIcon
          icon={isOpen ? faChevronUp : faChevronDown}
          style={{ fontSize: "12px", color: "#64748b" }}
        />
      </div>

      {isOpen && (
        <div style={{ padding: "14px", background: "#0f172a", position: "relative" }}>
          <button
            type="button"
            onClick={handleCopy}
            style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              background: copied ? "#16a34a" : "#334155",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              padding: "4px 10px",
              fontSize: "11px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "5px",
            }}
          >
            <FontAwesomeIcon icon={faCopy} /> {copied ? "Copied!" : "Copy JSON"}
          </button>
          <pre
            style={{
              color: "#38bdf8",
              fontSize: "11px",
              fontFamily: "monospace",
              margin: 0,
              maxHeight: "240px",
              overflowY: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

const CareparvaResponseModal = ({ isOpen, onClose, notification, modalType = "create" }) => {
  if (!isOpen || !notification) return null;

  const isCreate = modalType === "create";
  const currentStatusCode = isCreate ? notification.parva_create : notification.parva_update;
  const isPortfolio = Boolean(
    notification?.isPortfolio ||
    notification?.rebalance_date ||
    (notification?.datetime && !notification?.symbol)
  );

  const parseData = (raw) => {
    if (!raw) return null;
    if (typeof raw === "object") return raw;
    const str = String(raw).trim();
    if (!str) return null;
    try {
      return JSON.parse(str);
    } catch {
      const lines = str.split("\n").map((l) => l.trim()).filter(Boolean);
      const items = [];
      for (const line of lines) {
        try {
          items.push(JSON.parse(line));
        } catch {
          items.push({ rawString: line });
        }
      }
      return items.length === 1 ? items[0] : (items.length > 1 ? items : { rawString: str });
    }
  };

  /**
   * Recursively flattens any nested arrays or list structures so each update event
   * is guaranteed to be a single, independent update object.
   */
  const normalizeUpdates = (raw) => {
    if (!raw) return [];
    let parsed = raw;
    if (typeof raw === "string") {
      const str = raw.trim();
      if (!str) return [];
      try {
        parsed = JSON.parse(str);
      } catch {
        const lines = str.split("\n").map((l) => l.trim()).filter(Boolean);
        parsed = lines.map((l) => {
          try {
            return JSON.parse(l);
          } catch {
            return { rawString: l };
          }
        });
      }
    }

    const flatList = [];
    const flatten = (val) => {
      if (!val) return;
      if (Array.isArray(val)) {
        val.forEach(flatten);
      } else if (typeof val === "object") {
        flatList.push(val);
      } else {
        flatList.push({ rawString: String(val) });
      }
    };

    flatten(parsed);
    return flatList;
  };

  /**
   * Extracts the primary inner advice object from Careparva response structure:
   * Handles { data: { data: [ { ... } ] } } as well as plain objects.
   */
  const extractAdviceItem = (res) => {
    if (!res) return {};
    if (Array.isArray(res?.data?.data) && res.data.data.length > 0) return res.data.data[0];
    if (Array.isArray(res?.rawResponse?.data) && res.rawResponse.data.length > 0) return res.rawResponse.data[0];
    if (Array.isArray(res?.data) && res.data.length > 0) return res.data[0];
    if (res?.data && typeof res.data === "object" && !Array.isArray(res.data)) return res.data;
    if (res?.rawResponse && typeof res.rawResponse === "object") return res.rawResponse;
    return res;
  };

  /**
   * Cleans payload for Technical JSON Viewer by stripping duplicate outer wrappers.
   */
  const getCleanJson = (res) => {
    if (!res) return null;
    if (res.data) return res.data;
    if (res.rawResponse) return res.rawResponse;
    if (res.error) return { error: res.error, message: res.message };
    return res;
  };

  const parsedCreate = parseData(notification.parva_response);
  const createData = Array.isArray(parsedCreate) ? parsedCreate[0] : parsedCreate;
  const updatesArray = normalizeUpdates(notification.parva_update_response);

  const renderStatusBadge = (statusCode) => {
    if (statusCode === 1) {
      return (
        <span
          className="badge"
          style={{
            backgroundColor: "#dcfce7",
            color: "#15803d",
            border: "1px solid #86efac",
            padding: "4px 8px",
            fontSize: "12px",
            fontWeight: "600",
            borderRadius: "6px",
            display: "inline-flex",
            alignItems: "center",
            gap: "5px",
          }}
        >
          <FontAwesomeIcon icon={faCircleCheck} /> Success
        </span>
      );
    }
    if (statusCode === 2) {
      return (
        <span
          className="badge"
          style={{
            backgroundColor: "#fee2e2",
            color: "#b91c1c",
            border: "1px solid #fca5a5",
            padding: "4px 8px",
            fontSize: "12px",
            fontWeight: "600",
            borderRadius: "6px",
            display: "inline-flex",
            alignItems: "center",
            gap: "5px",
          }}
        >
          <FontAwesomeIcon icon={faCircleXmark} /> Error
        </span>
      );
    }
    return (
      <span
        className="badge"
        style={{
          backgroundColor: "#f1f5f9",
          color: "#64748b",
          border: "1px solid #cbd5e1",
          padding: "4px 8px",
          fontSize: "12px",
          fontWeight: "600",
          borderRadius: "6px",
          display: "inline-flex",
          alignItems: "center",
          gap: "5px",
        }}
      >
        <FontAwesomeIcon icon={faClock} /> Pending
      </span>
    );
  };

  const renderSingleResponseCard = (data, title, statusCode) => {
    if (!data) {
      return (
        <div
          style={{
            padding: "35px 20px",
            textAlign: "center",
            background: "#f8fafc",
            borderRadius: "8px",
            border: "1px dashed #cbd5e1",
            color: "#64748b",
          }}
        >
          <FontAwesomeIcon icon={faClock} style={{ fontSize: "32px", color: "#94a3b8", marginBottom: "12px" }} />
          <h5 style={{ margin: "0 0 6px 0", color: "#334155", fontWeight: "600" }}>No Response Data Available</h5>
          <p style={{ margin: 0, fontSize: "13px" }}>
            No response payload has been recorded from Careparva for this operation yet.
          </p>
        </div>
      );
    }

    const isError =
      statusCode === 2 ||
      data.success === false ||
      data.error ||
      data.status === "FAILED" ||
      data.status === "ERROR";

    const errorMessage =
      data.error ||
      data.message ||
      data.rawResponse?.message ||
      data.rawResponse?.error ||
      (typeof data.rawString === "string" ? data.rawString : null);

    const adviceItem = extractAdviceItem(data);
    const cleanJson = getCleanJson(data);

    return (
      <div>
        {/* Error Alert if Failed */}
        {isError && (
          <div
            style={{
              padding: "16px",
              background: "#fef2f2",
              border: "1px solid #f87171",
              borderRadius: "8px",
              marginBottom: "16px",
              color: "#991b1b",
            }}
          >
            <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                style={{ color: "#dc2626", fontSize: "20px", marginTop: "2px" }}
              />
              <div style={{ flex: 1 }}>
                <h5 style={{ margin: "0 0 4px 0", color: "#991b1b", fontWeight: "700" }}>
                  Careparva API Error
                </h5>
                <p style={{ margin: 0, fontSize: "13px", lineHeight: "1.5", wordBreak: "break-word" }}>
                  {typeof errorMessage === "object"
                    ? JSON.stringify(errorMessage, null, 2)
                    : errorMessage || "Failed to communicate with Careparva PDC endpoint."}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Structured Data Grid */}
        {(() => {
          const isPortfolioCard =
            isPortfolio ||
            Boolean(adviceItem?.PortfolioName) ||
            Boolean(adviceItem?.details && Array.isArray(adviceItem.details));

          if (isPortfolioCard) {
            return (
              <div
                style={{
                  background: "#fff",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  padding: "16px",
                  marginBottom: "12px",
                }}
              >
                {/* Success/Status message banner from response */}
                {(data?.data?.message || data?.rawResponse?.message || data?.message) && (
                  <div
                    style={{
                      padding: "8px 12px",
                      background: isError ? "#fef2f2" : "#f0fdf4",
                      border: `1px solid ${isError ? "#fca5a5" : "#86efac"}`,
                      borderRadius: "6px",
                      marginBottom: "14px",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: isError ? "#b91c1c" : "#15803d",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <FontAwesomeIcon icon={isError ? faCircleXmark : faCircleCheck} />
                    <span>
                      {data?.data?.message || data?.rawResponse?.message || data?.message}
                      {data?.data?.status ? ` (Status: ${data.data.status})` : ""}
                    </span>
                  </div>
                )}

                <div className="row g-3" style={{ display: "flex", flexWrap: "wrap", rowGap: "12px" }}>
                  <div className="col-md-4 col-sm-6">
                    <div style={{ fontSize: "11px", color: "#64748b", fontWeight: "600", textTransform: "uppercase" }}>
                      Advice Name / ID
                    </div>
                    <div style={{ fontSize: "13px", fontWeight: "600", color: "#1e293b", marginTop: "2px" }}>
                      {notification.unique_id || (notification.id ? `Timeline #${notification.id}` : "Portfolio Advice")}
                    </div>
                  </div>

                  <div className="col-md-4 col-sm-6">
                    <div style={{ fontSize: "11px", color: "#64748b", fontWeight: "600", textTransform: "uppercase" }}>
                      Portfolio / Service
                    </div>
                    <div style={{ fontSize: "13px", fontWeight: "600", color: "#1e293b", marginTop: "2px" }}>
                      {adviceItem?.PortfolioName ||
                        adviceItem?.ProductName ||
                        notification.portfolio_name ||
                        notification.service_name ||
                        "Portfolio"}
                    </div>
                  </div>

                  <div className="col-md-4 col-sm-6">
                    <div style={{ fontSize: "11px", color: "#64748b", fontWeight: "600", textTransform: "uppercase" }}>
                      Portfolio Type
                    </div>
                    <div style={{ fontSize: "13px", fontWeight: "600", marginTop: "2px" }}>
                      <span
                        style={{
                          background: "#e0f2fe",
                          color: "#0369a1",
                          border: "1px solid #bae6fd",
                          padding: "2px 8px",
                          borderRadius: "4px",
                          fontSize: "11px",
                          fontWeight: "700",
                          textTransform: "uppercase",
                        }}
                      >
                        {adviceItem?.PortfolioType || "EQUITY"}
                      </span>
                    </div>
                  </div>

                  <div className="col-md-4 col-sm-6">
                    <div style={{ fontSize: "11px", color: "#64748b", fontWeight: "600", textTransform: "uppercase" }}>
                      Rebalance Date
                    </div>
                    <div style={{ fontSize: "13px", fontWeight: "600", color: "#1e293b", marginTop: "2px" }}>
                      {notification.datetime
                        ? new Date(notification.datetime).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          })
                        : "N/A"}
                    </div>
                  </div>

                  <div className="col-md-4 col-sm-6">
                    <div style={{ fontSize: "11px", color: "#64748b", fontWeight: "600", textTransform: "uppercase" }}>
                      Total Constituents
                    </div>
                    <div style={{ fontSize: "13px", fontWeight: "700", color: "#0284c7", marginTop: "2px" }}>
                      {Array.isArray(adviceItem?.details) ? `${adviceItem.details.length} Stocks` : "N/A"}
                    </div>
                  </div>

                  <div className="col-md-4 col-sm-6">
                    <div style={{ fontSize: "11px", color: "#64748b", fontWeight: "600", textTransform: "uppercase" }}>
                      Stop Portfolio
                    </div>
                    <div style={{ fontSize: "13px", fontWeight: "600", marginTop: "2px" }}>
                      <span
                        style={{
                          background: adviceItem?.StopPortfolio === "YES" ? "#fee2e2" : "#dcfce7",
                          color: adviceItem?.StopPortfolio === "YES" ? "#b91c1c" : "#15803d",
                          border: `1px solid ${adviceItem?.StopPortfolio === "YES" ? "#fca5a5" : "#86efac"}`,
                          padding: "2px 8px",
                          borderRadius: "4px",
                          fontSize: "11px",
                          fontWeight: "700",
                        }}
                      >
                        {adviceItem?.StopPortfolio || "NO"}
                      </span>
                    </div>
                  </div>

                  {notification.message && (
                    <div className="col-12" style={{ borderTop: "1px solid #f1f5f9", paddingTop: "8px" }}>
                      <div style={{ fontSize: "11px", color: "#64748b", fontWeight: "600", textTransform: "uppercase" }}>
                        Rebalance Summary
                      </div>
                      <div
                        style={{ fontSize: "13px", color: "#334155", marginTop: "2px" }}
                        dangerouslySetInnerHTML={{ __html: notification.message }}
                      />
                    </div>
                  )}
                </div>

                {/* Constituents & Weightages Table */}
                {Array.isArray(adviceItem?.details) && adviceItem.details.length > 0 && (
                  <div style={{ marginTop: "16px" }}>
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: "700",
                        color: "#334155",
                        marginBottom: "8px",
                        textTransform: "uppercase",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span>Constituents & Weightages</span>
                      <span style={{ fontSize: "11px", color: "#64748b", fontWeight: "500" }}>
                        Total {adviceItem.details.length} stocks
                      </span>
                    </div>
                    <div
                      className="table-responsive"
                      style={{
                        border: "1px solid #e2e8f0",
                        borderRadius: "6px",
                        overflow: "hidden",
                      }}
                    >
                      <table className="table table-sm table-hover mb-0" style={{ fontSize: "12px" }}>
                        <thead style={{ background: "#f8fafc" }}>
                          <tr>
                            <th style={{ padding: "8px 12px", width: "45px", color: "#64748b" }}>#</th>
                            <th style={{ padding: "8px 12px", color: "#64748b" }}>Symbol</th>
                            <th style={{ padding: "8px 12px", color: "#64748b" }}>ISIN</th>
                            <th style={{ padding: "8px 12px", color: "#64748b", textAlign: "right" }}>Weightage</th>
                            <th style={{ padding: "8px 12px", color: "#64748b", textAlign: "center" }}>Exchange</th>
                            <th style={{ padding: "8px 12px", color: "#64748b", textAlign: "center" }}>Mutual Fund</th>
                          </tr>
                        </thead>
                        <tbody>
                          {adviceItem.details.map((item, idx) => {
                            const weightPercent =
                              item.Weightage !== undefined
                                ? `${(Number(item.Weightage) * 100).toFixed(2)}%`
                                : "N/A";
                            return (
                              <tr key={idx}>
                                <td style={{ padding: "8px 12px", color: "#64748b" }}>{idx + 1}</td>
                                <td style={{ padding: "8px 12px", fontWeight: "600", color: "#0f172a" }}>
                                  {item.Symbol || "N/A"}
                                </td>
                                <td style={{ padding: "8px 12px", color: "#475569", fontFamily: "monospace" }}>
                                  {item.ISIN || "N/A"}
                                </td>
                                <td style={{ padding: "8px 12px", textAlign: "right", fontWeight: "700", color: "#0284c7" }}>
                                  {weightPercent}
                                </td>
                                <td style={{ padding: "8px 12px", textAlign: "center" }}>
                                  <span
                                    style={{
                                      background: "#f1f5f9",
                                      border: "1px solid #e2e8f0",
                                      padding: "2px 6px",
                                      borderRadius: "4px",
                                      fontSize: "11px",
                                      fontWeight: "600",
                                      color: "#334155",
                                    }}
                                  >
                                    {item.ExchangeName || "NSE"}
                                  </span>
                                </td>
                                <td style={{ padding: "8px 12px", textAlign: "center" }}>
                                  <span
                                    style={{
                                      background: item.IfMutualFund === "YES" ? "#e0f2fe" : "#f1f5f9",
                                      color: item.IfMutualFund === "YES" ? "#0369a1" : "#64748b",
                                      border: `1px solid ${item.IfMutualFund === "YES" ? "#bae6fd" : "#e2e8f0"}`,
                                      padding: "2px 6px",
                                      borderRadius: "4px",
                                      fontSize: "11px",
                                      fontWeight: "600",
                                    }}
                                  >
                                    {item.IfMutualFund || "NO"}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          }

          /* Default Stock Trade Notification Grid */
          return (
            <div
              style={{
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                padding: "16px",
                marginBottom: "6px",
              }}
            >
              <div className="row g-3" style={{ display: "flex", flexWrap: "wrap", rowGap: "12px" }}>
                <div className="col-md-4 col-sm-6">
                  <div style={{ fontSize: "11px", color: "#64748b", fontWeight: "600", textTransform: "uppercase" }}>
                    Advice Name / ID
                  </div>
                  <div style={{ fontSize: "13px", fontWeight: "600", color: "#1e293b", marginTop: "2px" }}>
                    {adviceItem?.AdviceName ||
                      adviceItem?.adviceName ||
                      notification.unique_id ||
                      `SG${notification.id}`}
                  </div>
                </div>

                <div className="col-md-4 col-sm-6">
                  <div style={{ fontSize: "11px", color: "#64748b", fontWeight: "600", textTransform: "uppercase" }}>
                    Product / Service
                  </div>
                  <div style={{ fontSize: "13px", fontWeight: "600", color: "#1e293b", marginTop: "2px" }}>
                    {adviceItem?.ProductName || notification.service_name || "STREETGAINS"}
                  </div>
                </div>

                <div className="col-md-4 col-sm-6">
                  <div style={{ fontSize: "11px", color: "#64748b", fontWeight: "600", textTransform: "uppercase" }}>
                    Symbol / Exchange
                  </div>
                  <div style={{ fontSize: "13px", fontWeight: "600", color: "#1e293b", marginTop: "2px" }}>
                    {adviceItem?.Symbol || notification.symbol} ({adviceItem?.ExchangeName || notification.exchange || "NSE"})
                    {adviceItem?.ISIN && (
                      <span style={{ display: "block", fontSize: "11px", color: "#64748b" }}>
                        ISIN: {adviceItem.ISIN}
                      </span>
                    )}
                  </div>
                </div>

                <div className="col-md-4 col-sm-6">
                  <div style={{ fontSize: "11px", color: "#64748b", fontWeight: "600", textTransform: "uppercase" }}>
                    Action
                  </div>
                  <div style={{ fontSize: "13px", fontWeight: "600", marginTop: "2px" }}>
                    <span
                      style={{
                        color: (adviceItem?.BuyorSell === "BUY" || notification.action === 1) ? "#16a34a" : "#dc2626",
                        fontWeight: "700",
                      }}
                    >
                      {adviceItem?.BuyorSell || (notification.action === 1 ? "BUY / LONG" : "SELL / SHORT")}
                    </span>
                  </div>
                </div>

                <div className="col-md-4 col-sm-6">
                  <div style={{ fontSize: "11px", color: "#64748b", fontWeight: "600", textTransform: "uppercase" }}>
                    Entry Range / Price
                  </div>
                  <div style={{ fontSize: "13px", fontWeight: "600", color: "#1e293b", marginTop: "2px" }}>
                    {adviceItem?.EntryPriceStart && adviceItem?.EntryPriceEnd
                      ? `₹${adviceItem.EntryPriceStart} - ₹${adviceItem.EntryPriceEnd}`
                      : `₹${Number(notification.entry_price || 0).toFixed(2)}`}
                  </div>
                </div>

                <div className="col-md-4 col-sm-6">
                  <div style={{ fontSize: "11px", color: "#64748b", fontWeight: "600", textTransform: "uppercase" }}>
                    Target / Stoploss
                  </div>
                  <div style={{ fontSize: "13px", fontWeight: "600", color: "#1e293b", marginTop: "2px" }}>
                    T1: ₹{adviceItem?.TargetPrice || notification.target_price || "0"} | SL: ₹{adviceItem?.StopLoss || notification.stoploss || "0"}
                  </div>
                </div>

                {adviceItem?.PriceType && (
                  <div className="col-md-4 col-sm-6">
                    <div style={{ fontSize: "11px", color: "#64748b", fontWeight: "600", textTransform: "uppercase" }}>
                      Price Type
                    </div>
                    <div style={{ fontSize: "13px", fontWeight: "600", color: "#1e293b", marginTop: "2px" }}>
                      {adviceItem.PriceType === "PR" ? "Price Range (PR)" : adviceItem.PriceType}
                    </div>
                  </div>
                )}

                {(adviceItem?.ExitPrice !== undefined && Number(adviceItem.ExitPrice) > 0) || notification.cmp_price > 0 ? (
                  <div className="col-md-4 col-sm-6">
                    <div style={{ fontSize: "11px", color: "#64748b", fontWeight: "600", textTransform: "uppercase" }}>
                      CMP / Exit Price
                    </div>
                    <div style={{ fontSize: "13px", fontWeight: "600", color: "#1e293b", marginTop: "2px" }}>
                      ₹{Number(adviceItem?.ExitPrice || notification.cmp_price || 0).toFixed(2)}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          );
        })()}

        {/* Dedicated Independent JSON Viewer for this specific card */}
        <TechnicalJsonViewer data={cleanJson} label={`${title} JSON Response`} />
      </div>
    );
  };

  const renderUpdateResponses = () => {
    if (!updatesArray || updatesArray.length === 0) {
      return (
        <div
          style={{
            padding: "35px 20px",
            textAlign: "center",
            background: "#f8fafc",
            borderRadius: "8px",
            border: "1px dashed #cbd5e1",
            color: "#64748b",
          }}
        >
          <FontAwesomeIcon icon={faClock} style={{ fontSize: "32px", color: "#94a3b8", marginBottom: "12px" }} />
          <h5 style={{ margin: "0 0 6px 0", color: "#334155", fontWeight: "600" }}>No Update Requests Yet</h5>
          <p style={{ margin: 0, fontSize: "13px" }}>
            No modification or close advice updates have been dispatched to Careparva yet.
          </p>
        </div>
      );
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        {updatesArray.map((item, idx) => (
          <div
            key={idx}
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              padding: "16px",
              background: "#fff",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "12px",
                paddingBottom: "8px",
                borderBottom: "1px solid #f1f5f9",
              }}
            >
              <span style={{ fontWeight: "700", color: "#1e293b", fontSize: "13px" }}>
                <FontAwesomeIcon icon={faArrowRotateRight} className="me-1 text-primary" /> Update Event #{idx + 1}
              </span>
              {renderStatusBadge(item?.success === true ? 1 : item?.success === false ? 2 : notification.parva_update)}
            </div>
            {renderSingleResponseCard(item, `Update #${idx + 1}`, notification.parva_update)}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(15, 23, 42, 0.65)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 99999,
        padding: "15px",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: "#ffffff",
          borderRadius: "12px",
          width: "100%",
          maxWidth: "750px",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
          overflow: "hidden",
          animation: "fadeIn 0.2s ease-out",
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: isCreate
              ? (currentStatusCode === 2 ? "#fff1f2" : currentStatusCode === 1 ? "#f0fdf4" : "#f8fafc")
              : (currentStatusCode === 2 ? "#fff1f2" : (currentStatusCode === 1 || currentStatusCode > 0) ? "#f0fdf4" : "#f8fafc"),
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            {/* Status Icon */}
            <div
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "16px",
                background: currentStatusCode === 2 ? "#fee2e2" : (currentStatusCode === 1 || currentStatusCode > 0) ? "#dcfce7" : "#e2e8f0",
                color: currentStatusCode === 2 ? "#dc2626" : (currentStatusCode === 1 || currentStatusCode > 0) ? "#16a34a" : "#64748b",
                border: `1px solid ${
                  currentStatusCode === 2 ? "#fca5a5" : (currentStatusCode === 1 || currentStatusCode > 0) ? "#86efac" : "#cbd5e1"
                }`,
              }}
            >
              {currentStatusCode === 1 || currentStatusCode > 0 ? (
                <FontAwesomeIcon icon={faCheck} style={{ fontWeight: "bold" }} />
              ) : currentStatusCode === 2 ? (
                <FontAwesomeIcon icon={faXmark} style={{ fontWeight: "bold" }} />
              ) : (
                <FontAwesomeIcon icon={faClock} />
              )}
            </div>

            <div>
              <h4 style={{ margin: 0, fontSize: "16px", fontWeight: "700", color: "#0f172a" }}>
                {isPortfolio
                  ? (isCreate ? "Careparva Portfolio Create Status" : "Careparva Portfolio Rebalance Update History")
                  : (isCreate ? "Careparva Create Advice Status" : "Careparva Update Advice History")}
              </h4>
              <span style={{ fontSize: "12px", color: "#64748b" }}>
                {isPortfolio
                  ? `${notification.portfolio_name || notification.service_name || "Portfolio"} • ${notification.unique_id || `Timeline #${notification.id}`}`
                  : `${notification.symbol} (${notification.service_name || "Service"}) • ID: ${notification.unique_id || `SG${notification.id}`}`}
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              fontSize: "18px",
              color: "#64748b",
              cursor: "pointer",
              padding: "4px 8px",
              borderRadius: "4px",
            }}
            title="Close"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        {/* Modal Body */}
        <div
          style={{
            padding: "20px",
            overflowY: "auto",
            flex: 1,
            maxHeight: "calc(90vh - 80px)",
          }}
        >
          {isCreate
            ? renderSingleResponseCard(createData, "Create Advice", notification.parva_create)
            : renderUpdateResponses()}
        </div>
      </div>
    </div>
  );
};

export default CareparvaResponseModal;
